import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import * as signalR from '@microsoft/signalr';
import { format } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, filter, map, Observable, tap } from 'rxjs';
import { CoreMenuService } from 'src/@core/components/core-menu/core-menu.service';
import {
  Chat,
  ChatUnreadCount,
  MessageRequest,
  MessageResponse,
  NewChatsCount,
  Reaction,
  Reacts,
  ReadReceipt,
} from 'src/app/helpers/Interfaces/chat';
import { iEmployeeResultDto } from 'src/app/helpers/Interfaces/employee';
import { int, Nullable } from 'src/app/helpers/Types/types';
import { DataService } from 'src/app/helpers/data.service';
import { environment } from 'src/environments/environment.development';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  public chat$: Observable<Nullable<Chat>>;
  private _chatSource: BehaviorSubject<Nullable<Chat>>;

  public chats$: Observable<Array<Chat>>;
  private _chatsSource: BehaviorSubject<Array<Chat>>;

  public chatsLoading$: Observable<boolean>;
  private _chatsLoadingSource: BehaviorSubject<boolean>;

  public newMessage$: Observable<Nullable<MessageResponse>>;
  private _newMessageSource: BehaviorSubject<Nullable<MessageResponse>>;

  public newReaction$: Observable<Nullable<Reaction>>;
  private _newReactionSource: BehaviorSubject<Nullable<Reaction>>;

  public newReadReceipt$: Observable<Nullable<ReadReceipt>>;
  private _newReadReceiptSource: BehaviorSubject<Nullable<ReadReceipt>>;

  // Chat
  private readonly _getAllChat = `${environment.base_url}/api/Chat/GetWithRelationship`;
  private readonly _createChat = `${environment.base_url}/api/Chat/Add`;
  private readonly _chatCount = `${environment.base_url}/api/Chat/GetCount`;
  // Chat Employee
  private readonly _addChatEmployeeRange = `${environment.base_url}/api/ChatEmployee/AddRange`;
  // Messages
  private readonly _getAllMessages = `${environment.base_url}/api/Message/GetWithRelationship`;
  private readonly _sendMessages = `${environment.base_url}/api/Message/Add`;
  // Reactions
  private readonly _react = `${environment.base_url}/api/Reaction/Add`;
  private readonly _updateReact = `${environment.base_url}/api/Reaction/Update`;
  private readonly _deleteReact = `${environment.base_url}/api/Reaction/Remove`;
  // Read Receipt
  private readonly _markAsRead = `${environment.base_url}/api/ReadReceipt/AddRange`;

  // Sockets
  private readonly _messageHub = `${environment.base_url}/messageHub`;

  public connection = new signalR.HubConnectionBuilder()
    .withUrl(this._messageHub)
    .configureLogging(signalR.LogLevel.None)
    .build();

  constructor(
    private _http: HttpClient,
    private _dataServ: DataService,
    private _toastrServ: ToastrService,
    private _menuServ: CoreMenuService
  ) {
    this._chatSource = new BehaviorSubject<Nullable<Chat>>(null);
    this.chat$ = this._chatSource.asObservable();

    this._chatsSource = new BehaviorSubject<Array<Chat>>([]);
    this.chats$ = this._chatsSource.asObservable();

    this._chatsLoadingSource = new BehaviorSubject<boolean>(false);
    this.chatsLoading$ = this._chatsLoadingSource.asObservable();

    this._newMessageSource = new BehaviorSubject<Nullable<MessageResponse>>(
      null
    );
    this.newMessage$ = this._newMessageSource.asObservable();

    this._newReactionSource = new BehaviorSubject<Nullable<Reaction>>(null);
    this.newReaction$ = this._newReactionSource.asObservable();

    this._newReadReceiptSource = new BehaviorSubject<Nullable<ReadReceipt>>(
      null
    );
    this.newReadReceipt$ = this._newReadReceiptSource.asObservable();

    this.connection.onclose(async () => {
      await this.start();
    });

    this.connection.on('MessageUpdated', (message: MessageResponse) => {
      if (
        message.chat?.chatEmployees?.find(
          (ce) => ce.employeeId === this._dataServ.employeeValue?.id
        ) &&
        message.senderId !== this._dataServ.employeeValue?.id
      ) {
        if (this.chatValue?.id !== message.chatId || !document.hasFocus()) {
          const data = {
            title: `${message.sender?.firstName} ${message.sender?.lastName} messaged you.`,
            content: message.text,
          };
          this.notifyMe(data);
        }
      }
      this.setMessage(this._processMessage(message));
      this._processSentMessage(message);
    });
    this.connection.on('ChatUpdated', (chat: Chat) => {
      if (
        chat.chatEmployees?.find(
          (x) => x.employeeId === _dataServ.employeeValue.id
        ) &&
        chat.createdBy !== _dataServ.employeeValue.id
      ) {
        if (!this.chatsValue.find((c) => c.id === chat.id)) {
          this.setChats([this._processChat(chat), ...this.chatsValue]);
        }
      }
    });

    // this.connection.on('ChatEmployeeUpdated', (chatEmployee: ChatEmployee) => {
    //   console.log(chatEmployee, 'chat employee');
    // });
    this.connection.on('ReactionUpdated', (reaction: Reaction) => {
      this._newReactionSource.next(reaction);
    });

    this.connection.on('ReadReceiptUpdated', (readReceipt: ReadReceipt) => {
      this._newReadReceiptSource.next(readReceipt);
    });
    this.connection.on('UnreadUpdated', (chatUnreadCount: ChatUnreadCount) => {
      // console.log(chatUnreadCount);

      const update = () => {
        const chatIndex = this.chatsValue.findIndex(
          (x) => x.id === chatUnreadCount.chatId
        );
        if (~chatIndex) {
          const tcs = [...this.chatsValue];
          if (
            chatUnreadCount.unReadCount !== undefined &&
            chatUnreadCount.unReadCount !== null
          ) {
            tcs[chatIndex].unreadCount =
              (tcs[chatIndex].unreadCount || 0) + chatUnreadCount.unReadCount;
          }
          this.setChats(tcs);
        }
      };

      if (~(chatUnreadCount.unReadCount as int))
        if (chatUnreadCount.employeeId !== _dataServ.employeeValue?.id)
          update();
        else return;
      else {
        if (chatUnreadCount.employeeId === _dataServ.employeeValue?.id)
          update();
      }
    });
    this.connection.on('ChatCountUpdated', (newChatCount: NewChatsCount) => {
      // console.log(newChatCount);
      if (newChatCount.employeeId === _dataServ.employeeValue.id) {
        this._handleChatCount(newChatCount.count);
      }
    });
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> | Promise<any> | any {
    this.start();
  }

  public async start() {
    if (this._dataServ.employeeValue && this.connection.state == 'Disconnected')
      try {
        await this.connection.start();
      } catch (err) {
        console.warn(err);
        setTimeout(() => this.start(), 5000);
      }
  }

  get chatValue(): Nullable<Chat> {
    return this._chatSource.value;
  }
  setChat(value: Nullable<Chat>) {
    this._chatSource.next(value);
  }

  get newMessageValue(): Nullable<MessageResponse> {
    return this._newMessageSource.value;
  }
  setMessage(value: Nullable<MessageResponse>) {
    this._newMessageSource.next(value);
  }

  get chatsValue(): Array<Chat> {
    return this._chatsSource.value;
  }
  setChats(value: Array<Chat>) {
    this._chatsSource.next(value);
  }

  get chatsLoadingValue(): boolean {
    return this._chatsLoadingSource.value;
  }
  setChatsLoading(value: boolean) {
    this._chatsLoadingSource.next(value);
  }

  private _processChat(chat: Chat): Chat {
    chat.chatEmployees = chat.chatEmployees?.map((emp) => {
      const employee: iEmployeeResultDto = {
        ...emp.employee!,
        name: null,
        Type: null,
      };
      // employee.Type = RoleTypes[employee.type];
      employee.name = `${employee.firstName} ${employee.lastName}`;
      employee.Type = employee.type;
      if (!employee.reportsToProfile?.includes(environment.base_url)) {
        employee.reportsToProfile = `${environment.base_url}/${employee.reportsToProfile}`;
      }
      if (!employee.profilePic?.includes(environment.base_url)) {
        employee.profilePic = `${environment.base_url}/${employee.profilePic}`;
      }
      if (employee.id !== this._dataServ.employeeValue?.id) {
        chat.title = employee.name;
        chat.icon = employee.profilePic;
      }
      return { ...emp, employee };
    });
    if (chat.lastMessage && chat.lastMessage?.createdDate) {
      chat.timeStamp = format(chat.lastMessage.createdDate, 'p');
      chat.lastMessage = this._processMessage(chat.lastMessage);
    }
    return chat;
  }
  private _processMessage(message: MessageResponse): MessageResponse {
    // if (message.id === '0fa1d5c7-0e8d-461a-56f5-08dcbaa84977') debugger;
    message.createdDate = format(message.createdDate as string, 'P p');
    message.cd = format(message.createdDate as string, 'p');
    message.reacts = this._processReactions(message.reactions);
    if (!message.content?.includes(environment.base_url))
      message.content = `${environment.base_url}/${message.content}`;

    if (message.contentType?.match('video')) message.type = 'video';
    else if (message.contentType?.match('image')) message.type = 'image';
    else message.type = 'doc';

    if (message.contentName) {
      const sp = message.contentName.split('|');
      message.contentName = sp[0];
      message.contentSize = sp[1];
    }

    return message;
  }

  notifyMe(data: { title: string; content?: Nullable<string> }) {
    if (!window.Notification) {
      console.log('Browser does not support notifications.');
    } else {
      const notify = () => {
        const options: NotificationOptions = {
          icon: '/assets/images/logo/logo.2.png',
          silent: true,
          // requireInteraction: true,
        };
        if (data.content) options.body = data.content;
        const noti = new Notification(data.title, options);
        const audio = new Audio('/assets/audio/notification.mp3');
        audio.play();
      };
      const ask = () => {
        Notification.requestPermission()
          .then(() => {
            notify();
          })
          .catch(() => {
            ask();
          });
      };
      if (Notification.permission === 'granted') {
        notify();
      } else {
        ask();
      }
    }
  }

  public _processReactions(reactions?: Array<Reaction>): Reacts {
    const reacts: Reacts = {
      emojis: [],
      length: 0,
    };
    if (reactions && reactions.length > 0) {
      const emojiSet = new Set<Nullable<string> | undefined>();
      reactions.forEach((reaction) => emojiSet.add(reaction.emoji));
      const emojis = Array.from(emojiSet).map((emoji) => ({ emoji }));
      if (reactions.length > 1) {
        reacts.length += reactions.length;
      }
      for (let i = 0; i < emojis.length; i++) {
        const emoji = emojis[i];
        if (i > 3) break;
        reacts.emojis.push(emoji.emoji);
      }
    }
    return reacts;
  }
  private _processSentMessage(message: MessageResponse): MessageResponse {
    const chatIndex = this.chatsValue.findIndex(
      (chat) => chat.id === message.chatId
    );
    if (~chatIndex) {
      const tcs = [...this.chatsValue];
      tcs[chatIndex].lastMessage = message;
      tcs[chatIndex].lastMessageId = message.id;
      if (message.createdDate) {
        tcs[chatIndex].timeStamp = format(message.createdDate, 'p');
      }
      this.setChats(tcs);
    }
    return message;
  }

  // Chat
  GetAllChat() {
    this.setChatsLoading(true);
    return this._http
      .get<Array<Chat>>(
        `${this._getAllChat}?EmployeeId=${this._dataServ.employeeValue?.id}`,
        {
          responseType: 'json',
        }
      )
      .pipe(map((chats) => chats.map((chat) => this._processChat(chat))))
      .subscribe({
        next: (chats) => {
          this.setChats(chats);
        },
        error: () => {
          this._toastrServ.error('Something went wrong while fetching chats.');
        },
        complete: () => {
          this.setChatsLoading(false);
        },
      });
  }
  CreateChat(payload: Chat) {
    return this._http
      .post<Chat>(this._createChat, payload, {
        responseType: 'json',
      })
      .pipe(
        map((chat) => {
          this.setChats([this._processChat(chat), ...this.chatsValue]);
          return chat;
        })
      );
  }
  // Messages
  GetAllMessages(chatId?: Nullable<string>) {
    return this._http
      .get<Array<MessageResponse>>(`${this._getAllMessages}?ChatId=${chatId}`, {
        responseType: 'json',
      })
      .pipe(
        map((messages) =>
          messages.map((message) => this._processMessage(message))
        )
      );
  }
  SendMessages(payload: MessageRequest) {
    const pl = new FormData();
    pl.set('ChatId', `${payload.chatId}`);
    pl.set('SenderId', `${payload.senderId}`);
    if (payload.text) pl.set('Text', payload.text);
    if (payload.content) pl.set('Content', payload.content);
    if (payload.contentType) pl.set('ContentType', payload.contentType);
    if (payload.contentName) pl.set('ContentName', payload.contentName);
    return this._http.post<MessageResponse>(this._sendMessages, pl, {
      responseType: 'json',
    });
  }
  // Read Receipt
  MarkAsRead(payload: Array<ReadReceipt>) {
    return this._http.post<Array<ReadReceipt>>(this._markAsRead, payload, {
      responseType: 'json',
    });
  }
  // React
  React(payload: Reaction) {
    return this._http.post<Reaction>(this._react, payload, {
      responseType: 'json',
    });
  }
  UpdateReact(payload: Reaction) {
    return this._http.put<Reaction>(this._updateReact, payload, {
      responseType: 'json',
    });
  }
  DeleteReact(reactionId?: Nullable<string>) {
    return this._http.delete(`${this._deleteReact}?id=${reactionId}`, {
      responseType: 'json',
    });
  }
  FetchChatCount() {
    this._http
      .get<int>(
        `${this._chatCount}?EmployeeId=${this._dataServ.employeeValue?.id}`,
        { responseType: 'json' }
      )
      .subscribe({
        next: (res) => {
          // console.log(res);
          if (res) {
            this._handleChatCount(res);
          }
        },
      });
  }
  private _handleChatCount(count: number) {
    // debugger;
    if (count) {
      const k = this._menuServ._currentMenuKey;
      const menu = this._menuServ.getCurrentMenu();
      if (menu) {
        const i = menu.findIndex((x) => x.id == 'chat');
        if (menu[i]) {
          menu[i]!.badge = {
            title: count.toString(),
            classes: 'badge-danger badge-pill',
          };
        }
      }
      this._menuServ._registry[k] = menu;
      this._menuServ.setCurrentMenu(k);
    }
  }
  downloadFile(
    url: string,
    onProgress: (progress: number) => void
  ): Observable<Blob> {
    return this._http
      .get(url, {
        reportProgress: true,
        observe: 'events',
        responseType: 'blob',
      })
      .pipe(
        tap((event: HttpEvent<Blob>) => {
          if (event.type === HttpEventType.DownloadProgress) {
            const total = event.total || 1;
            const progress = Math.round((event.loaded / total) * 100);
            onProgress(progress);
          }
        }),
        filter(
          (event: HttpEvent<Blob>): event is HttpResponse<Blob> =>
            event.type === HttpEventType.Response
        ),
        map((event: HttpResponse<Blob>) => event.body as Blob)
      );
  }
}
